// `comms`: Swift `CommMessage` (GroundTeam/Models/Engagement.swift): the texts, emails and calls logged with a record.
// `channel` is typed `ActivityType` in Swift, so any activity type decodes, though the app only writes text / email /
// call (COMM_CHANNELS). Decoding template `CommMessage()` (GroundTeam/Sync/ResilientDecoding.swift:126).

import { ACTIVITY_TYPES, COMM_DIRECTIONS, type CommChannel, type CommDirection } from '../enums';
import type { JsonObject } from '../wire';
import { newId } from '../uuid';
import {
  BOOL,
  DATE,
  STRING,
  UUID,
  createModel,
  decodeModel,
  enumOf,
  field,
  nowWire,
  type Decoded,
  type FieldMap,
  type Known,
  type Passthrough,
} from './fields';

export interface CommPayload extends Passthrough {
  id: string;
  recordId?: string;
  userId?: string;
  channel: CommChannel;
  direction: CommDirection;
  subject: string;
  body: string;
  sentAt: string;
  isRead: boolean;
  templateId?: string;
  isAutomated: boolean;
}

export const COMM_KEYS = [
  'id',
  'recordId',
  'userId',
  'channel',
  'direction',
  'subject',
  'body',
  'sentAt',
  'isRead',
  'templateId',
  'isAutomated',
] as const satisfies readonly (keyof Known<CommPayload>)[];

export const COMM_FIELDS: FieldMap<CommPayload> = {
  id: field.strict(UUID),
  recordId: field.opt(UUID),
  userId: field.opt(UUID),
  channel: field.req(enumOf('ActivityType', ACTIVITY_TYPES), () => 'text'),
  direction: field.req(enumOf('CommDirection', COMM_DIRECTIONS), () => 'outbound'),
  subject: field.req(STRING, () => ''),
  body: field.req(STRING, () => ''),
  sentAt: field.req(DATE, nowWire),
  isRead: field.req(BOOL, () => true),
  templateId: field.opt(UUID),
  isAutomated: field.req(BOOL, () => false),
};

export function decodeComm(raw: JsonObject, envelopeId?: string): Decoded<CommPayload> {
  return decodeModel(COMM_FIELDS, raw, {}, envelopeId);
}

/** `CommMessage()` has a default for every key. */
export type CommInit = Partial<Known<CommPayload>>;

export function newComm(init: CommInit = {}): CommPayload {
  const defaults = {
    id: newId(),
    channel: 'text',
    direction: 'outbound',
    subject: '',
    body: '',
    sentAt: nowWire(),
    isRead: true,
    isAutomated: false,
  };
  return createModel(COMM_FIELDS, defaults, init, 'comms');
}
